/**
 * Timezone Utility Functions for Frontend
 * Handles Indian Standard Time (IST) and automatic localization based on user's browser settings
 */

// IST is UTC+5:30
const IST_OFFSET_HOURS = 5;
const IST_OFFSET_MINUTES = 30;
const IST_OFFSET_MS = (IST_OFFSET_HOURS * 60 + IST_OFFSET_MINUTES) * 60 * 1000;
const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Get user's browser locale
 * @returns {string} User's locale (e.g., 'en-IN', 'en-US')
 */
export function getUserLocale(): string {
  return navigator.language || 'en-IN';
}

/**
 * Get user's browser timezone
 * @returns {string} User's timezone (e.g., 'Asia/Kolkata', 'America/New_York')
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get current time in IST
 * @returns {Date} Current date/time in IST
 */
export function getCurrentIST(): Date {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  return new Date(utc + IST_OFFSET_MS);
}

/**
 * Convert any date to IST
 * @param {Date|string|number} date - Date to convert
 * @returns {Date} Date in IST
 */
export function toIST(date: Date | string | number): Date {
  const dateObj = new Date(date);
  const utc = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000);
  return new Date(utc + IST_OFFSET_MS);
}

/**
 * Format date in IST with automatic localization based on user's browser
 * @param {Date|string|number} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @param {boolean} forceIST - Force IST timezone (default: true)
 * @returns {string} Formatted date string
 */
export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {},
  forceIST: boolean = true
): string {
  const dateObj = new Date(date);
  const locale = getUserLocale();
  const timezone = forceIST ? IST_TIMEZONE : getUserTimezone();

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
}

/**
 * Format datetime in IST with automatic localization based on user's browser
 * @param {Date|string|number} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @param {boolean} forceIST - Force IST timezone (default: true)
 * @returns {string} Formatted datetime string
 */
export function formatDateTime(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {},
  forceIST: boolean = true
): string {
  const dateObj = new Date(date);
  const locale = getUserLocale();
  const timezone = forceIST ? IST_TIMEZONE : getUserTimezone();

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
}

/**
 * Format time in IST with automatic localization
 * @param {Date|string|number} date - Date to format
 * @param {boolean} forceIST - Force IST timezone (default: true)
 * @returns {string} Formatted time string
 */
export function formatTime(
  date: Date | string | number,
  forceIST: boolean = true
): string {
  const dateObj = new Date(date);
  const locale = getUserLocale();
  const timezone = forceIST ? IST_TIMEZONE : getUserTimezone();

  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
}

/**
 * Format date in short format (e.g., "12/16/2025" or "16/12/2025" based on locale)
 * @param {Date|string|number} date - Date to format
 * @param {boolean} forceIST - Force IST timezone (default: true)
 * @returns {string} Formatted date string
 */
export function formatDateShort(
  date: Date | string | number,
  forceIST: boolean = true
): string {
  const dateObj = new Date(date);
  const locale = getUserLocale();
  const timezone = forceIST ? IST_TIMEZONE : getUserTimezone();

  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dateObj);
}

/**
 * Format date relative to now (e.g., "2 hours ago", "in 3 days")
 * @param {Date|string|number} date - Date to format
 * @param {boolean} forceIST - Force IST timezone (default: true)
 * @returns {string} Relative time string
 */
export function formatRelativeTime(
  date: Date | string | number,
  forceIST: boolean = true
): string {
  const dateObj = new Date(date);
  const now = forceIST ? getCurrentIST() : new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  const locale = getUserLocale();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSeconds) < 60) {
    return rtf.format(diffSeconds, 'second');
  } else if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  } else if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, 'day');
  } else {
    return formatDate(date, {}, forceIST);
  }
}

/**
 * Get IST date string (YYYY-MM-DD)
 * @param {Date|string|number} date - Date to format
 * @returns {string} Date string in IST
 */
export function getISTDateString(date: Date | string | number = new Date()): string {
  const istDate = toIST(date);
  const year = istDate.getFullYear();
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const day = String(istDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get IST time string (HH:MM:SS)
 * @param {Date|string|number} date - Date to format
 * @returns {string} Time string in IST
 */
export function getISTTimeString(date: Date | string | number = new Date()): string {
  const istDate = toIST(date);
  const hours = String(istDate.getHours()).padStart(2, '0');
  const minutes = String(istDate.getMinutes()).padStart(2, '0');
  const seconds = String(istDate.getSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Get IST datetime string (YYYY-MM-DD HH:MM:SS)
 * @param {Date|string|number} date - Date to format
 * @returns {string} Datetime string in IST
 */
export function getISTDateTimeString(date: Date | string | number = new Date()): string {
  return `${getISTDateString(date)} ${getISTTimeString(date)}`;
}

/**
 * Check if a date is today in IST
 * @param {Date|string|number} date - Date to check
 * @returns {boolean}
 */
export function isToday(date: Date | string | number): boolean {
  const todayIST = getISTDateString();
  const dateIST = getISTDateString(date);
  return todayIST === dateIST;
}

/**
 * Check if a date is in the past (in IST)
 * @param {Date|string|number} date - Date to check
 * @returns {boolean}
 */
export function isPast(date: Date | string | number): boolean {
  const dateObj = new Date(date);
  const now = getCurrentIST();
  return dateObj < now;
}

/**
 * Check if a date is in the future (in IST)
 * @param {Date|string|number} date - Date to check
 * @returns {boolean}
 */
export function isFuture(date: Date | string | number): boolean {
  const dateObj = new Date(date);
  const now = getCurrentIST();
  return dateObj > now;
}

/**
 * Get start of day in IST
 * @param {Date|string|number} date - Date to process
 * @returns {Date} Start of day in IST (00:00:00)
 */
export function getStartOfDayIST(date: Date | string | number = new Date()): Date {
  const istDate = toIST(date);
  istDate.setHours(0, 0, 0, 0);
  return istDate;
}

/**
 * Get end of day in IST
 * @param {Date|string|number} date - Date to process
 * @returns {Date} End of day in IST (23:59:59.999)
 */
export function getEndOfDayIST(date: Date | string | number = new Date()): Date {
  const istDate = toIST(date);
  istDate.setHours(23, 59, 59, 999);
  return istDate;
}

/**
 * Add days to a date in IST
 * @param {Date|string|number} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date in IST
 */
export function addDaysIST(date: Date | string | number, days: number): Date {
  const istDate = toIST(date);
  istDate.setDate(istDate.getDate() + days);
  return istDate;
}

/**
 * Get the IST timezone name
 * @returns {string} "Asia/Kolkata"
 */
export function getISTTimezoneName(): string {
  return IST_TIMEZONE;
}

/**
 * Get IST offset string
 * @returns {string} "+05:30"
 */
export function getISTOffsetString(): string {
  return '+05:30';
}

/**
 * Format date for input[type="datetime-local"] in IST
 * @param {Date|string|number} date - Date to format
 * @returns {string} Formatted string for datetime-local input
 */
export function formatDateTimeLocal(date: Date | string | number = new Date()): string {
  const istDate = toIST(date);
  const year = istDate.getFullYear();
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const day = String(istDate.getDate()).padStart(2, '0');
  const hours = String(istDate.getHours()).padStart(2, '0');
  const minutes = String(istDate.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Format currency based on user's locale (defaults to Indian Rupee)
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'INR'): string {
  const locale = getUserLocale();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

/**
 * Format number based on user's locale
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export function formatNumber(num: number): string {
  const locale = getUserLocale();
  return new Intl.NumberFormat(locale).format(num);
}

/**
 * Convert IST datetime-local input value to UTC ISO string for server storage
 * Use this when submitting datetime-local input values to the backend
 * @param {string} istDateTimeLocal - Value from datetime-local input (YYYY-MM-DDTHH:MM)
 * @returns {string} UTC ISO string
 */
export function istDateTimeLocalToUTC(istDateTimeLocal: string): string {
  if (!istDateTimeLocal) return '';

  // Parse the date parts manually to avoid timezone interpretation issues
  // Format expected: YYYY-MM-DDTHH:MM or YYYY-MM-DD HH:MM
  const dateStr = istDateTimeLocal.replace(' ', 'T');
  const parts = dateStr.split('T');

  if (parts.length !== 2) {
    console.error('Invalid datetime format:', istDateTimeLocal);
    return '';
  }

  const [datePart, timePart] = parts;
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // Create a date object treating the input as IST (not local browser time)
  // We do this by creating a UTC date and then subtracting IST offset
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));

  // Subtract 5.5 hours to convert from IST to UTC
  const utcDateTime = new Date(utcDate.getTime() - (5.5 * 60 * 60 * 1000));

  return utcDateTime.toISOString();
}

/**
 * Convert UTC date to IST and format for datetime-local input
 * Use this when loading UTC dates from backend to display in datetime-local inputs
 * @param {Date|string|number} utcDate - UTC date from backend
 * @returns {string} IST datetime string for datetime-local input (YYYY-MM-DDTHH:MM)
 */
export function utcToISTDateTimeLocal(utcDate: Date | string | number): string {
  if (!utcDate) return '';

  const date = new Date(utcDate);
  // Add 5.5 hours to convert UTC to IST
  const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));

  const year = istDate.getFullYear();
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const day = String(istDate.getDate()).padStart(2, '0');
  const hours = String(istDate.getHours()).padStart(2, '0');
  const minutes = String(istDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export default {
  getUserLocale,
  getUserTimezone,
  getCurrentIST,
  toIST,
  formatDate,
  formatDateTime,
  formatTime,
  formatDateShort,
  formatRelativeTime,
  getISTDateString,
  getISTTimeString,
  getISTDateTimeString,
  isToday,
  isPast,
  isFuture,
  getStartOfDayIST,
  getEndOfDayIST,
  addDaysIST,
  getISTTimezoneName,
  getISTOffsetString,
  formatDateTimeLocal,
  formatCurrency,
  formatNumber,
  istDateTimeLocalToUTC,
  utcToISTDateTimeLocal,
};
